import Papa from 'papaparse'
import { Dayjs } from 'dayjs'
import { ColumnType, SortOrder } from 'antd/es/table/interface'
import { saveCsv } from '../../services/fileSystemService'

// A table column that can also export a money value as a plain number, so the
// file is readable (formatted column) and pivot-able (numeric column).
export type CsvColumn<T> = ColumnType<T> & {
  exportCents?: (record: T) => number | null | undefined
  // Overrides render for the file, when the screen shows a placeholder (e.g. "—") the CSV should not.
  exportValue?: (record: T) => string | number | null | undefined
}

export type CsvDateRange = {
  start: Dayjs
  end: Dayjs
}

// Mirrors the backend's ExcelSafeStringConverter: a cell that starts with one of
// these is treated as a formula by Excel/Sheets, so prefix it with a quote.
const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r']

// Formatted numbers such as "-$1,234.50" or "-12.5%" start with "-" but are data, not formulas.
const NUMERIC_VALUE = /^-?\$?[\d,]*\.?\d+%?$/

export function excelSafe(value: string): string {
  if (NUMERIC_VALUE.test(value)) {
    return value
  }
  return FORMULA_PREFIXES.some(prefix => value.startsWith(prefix)) ? `'${value}` : value
}

// Same lookup antd uses: a nested path for an array dataIndex, and the whole record when there is none.
function dataIndexValue<T>(record: T, dataIndex: CsvColumn<T>['dataIndex']): unknown {
  if (dataIndex === undefined || dataIndex === null) {
    return record
  }
  const path: (string | number)[] = Array.isArray(dataIndex) ? dataIndex.map(key => key as string | number) : [dataIndex as string | number]
  return path.reduce<unknown>((value, key) => (value as Record<string | number, unknown> | null | undefined)?.[key], record)
}

function cellValue<T>(column: CsvColumn<T>, record: T, index: number): string | number {
  const hasDataIndex = column.dataIndex !== undefined && column.dataIndex !== null
  const raw = dataIndexValue(record, column.dataIndex)
  const value = column.exportValue
    ? column.exportValue(record)
    : column.render ? column.render(raw, record, index) : hasDataIndex ? raw : undefined

  // A render that returns markup (a React element) has no text for the file, so fall back to the raw value.
  const exported = typeof value === 'object' && value !== null ? raw : value
  if (exported === null || exported === undefined) {
    return ''
  }
  if (typeof exported === 'number') {
    return exported
  }
  return excelSafe(String(exported))
}

// A fixed order for an export, using the same comparator a table column sorts with.
export type TableSort<T> = {
  compare: (a: T, b: T, sortOrder?: SortOrder) => number
  order: 'ascend' | 'descend'
}

// Mirrors antd's own sorting: descending negates the comparator rather than reversing, so ties keep their order.
export function sortForExport<T>(rows: T[], sort: TableSort<T> | undefined): T[] {
  if (!sort) {
    return rows
  }
  return [...rows].sort((a, b) => {
    const result = sort.compare(a, b, sort.order)
    return sort.order === 'descend' ? -result : result
  })
}

function centsToDollars(cents: number | null | undefined): number | string {
  if (cents === null || cents === undefined || isNaN(cents)) {
    return ''
  }
  return Math.round(cents) / 100
}

// Builds a CSV from the same antd column definitions the table renders, so the
// export always matches the on-screen columns and formatting.
export function tableToCsv<T>(columns: CsvColumn<T>[], rows: T[]): string {
  const fields = columns.flatMap(column => {
    const title = String(column.title ?? '')
    return column.exportCents ? [title, `${title} (USD)`] : [title]
  })
  const data = rows.map((record, index) => columns.flatMap(column => {
    const formatted = cellValue(column, record, index)
    return column.exportCents ? [formatted, centsToDollars(column.exportCents(record))] : [formatted]
  }))

  return Papa.unparse({ fields, data })
}

export function reportCsvFilename(reportName: string, dateRange: CsvDateRange): string {
  const name = reportName.trim().replace(/[^A-Za-z0-9]+/g, '-')

  return `${name}-${dateRange.start.format('YYYY-MM-DD')}-${dateRange.end.format('YYYY-MM-DD')}.csv`
}

// Excel reads a CSV without a byte-order mark as ANSI, garbling accented names.
const UTF8_BOM = '\uFEFF'

export function exportTableToCsv<T>(reportName: string, dateRange: CsvDateRange, columns: CsvColumn<T>[], rows: T[]) {
  saveCsv(UTF8_BOM + tableToCsv(columns, rows), reportCsvFilename(reportName, dateRange))
}
